import { ControlPogResetModRow } from '../../types';

// Confirming a task does not change a mod's task id — the backend falls back to the most
// recent closed task, so the link stays correct. The cache only goes stale when a *new* task
// opens for a mod, hence the short TTL and the manual refresh rather than eager invalidation.
// Token-bound and per-store, matching the store/camera caches in the API layer.
const TASK_ID_CACHE_DURATION = 2 * 60 * 1000;

const taskIdCacheKey = (storeName: string) => `pogReset.installMap.taskIds:${storeName}`;

/** Reduce mod rows to the only thing the map's dot links need. */
export const taskIdsByTag = (rows: ControlPogResetModRow[]): Record<string, number> => {
  const byTag: Record<string, number> = {};
  rows.forEach((row) => {
    const taskId = row.suggested?.taskId ?? row.taskId;
    if (row.tagName && taskId != null) {
      byTag[row.tagName] = taskId;
    }
  });

  return byTag;
};

export const readTaskIdCache = (
  storeName: string,
  token: string
): Record<string, number> | null => {
  const key = taskIdCacheKey(storeName);
  try {
    const cached = localStorage.getItem(key);
    if (!cached) {
      return null;
    }

    const { data, timestamp, token: cachedToken } = JSON.parse(cached);
    if (cachedToken === token && Date.now() - timestamp < TASK_ID_CACHE_DURATION) {
      return data;
    }

    localStorage.removeItem(key);
    return null;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

export const writeTaskIdCache = (
  storeName: string,
  token: string,
  data: Record<string, number>
): void => {
  // An empty map means the mapping run has not been calculated yet, not that these mods have
  // no tasks. Caching it would leave every dot unlinkable until the TTL expired, so skip the
  // write and let the next load ask again.
  if (Object.keys(data).length === 0) {
    return;
  }

  try {
    localStorage.setItem(
      taskIdCacheKey(storeName),
      JSON.stringify({ data, timestamp: Date.now(), token })
    );
  } catch {
    // Storage full or unavailable — links still work, they just refetch next time.
  }
};

/** Backs the map's manual "Refresh task links" action. */
export const clearTaskIdCache = (storeName: string): void => {
  localStorage.removeItem(taskIdCacheKey(storeName));
};
